import React, { useState } from 'react';
import { z } from 'zod';
import { OktaIntegrationType } from '@glueapp/graph-types';
import { useIntegrationAccessApi } from '../../api/useIntegrationAccessApi';
import { useZodForm } from '../../helpers/useZodForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

interface Props {
    integration: OktaIntegrationType;
    label: string;
}

const oktaIntegrationSchema = z.object({
    clientId: z.string().nonempty(),
    clientSecret: z.string().nonempty(),
    oktaDomain: z.string().nonempty().url(),
});

type OktaIntegrationSchemaType = z.infer<typeof oktaIntegrationSchema>;

export const OktaIntegrationAuthForm: React.FC<Props> = ({ integration, label }) => {
    const [showInputs, setShowInputs] = useState(false);
    const { createOktaIntegrationAccess } = useIntegrationAccessApi();
    const queryClient = useQueryClient();

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useZodForm({ schema: oktaIntegrationSchema });

    const toggleInputs = () => {
        setShowInputs(prevShowInputs => !prevShowInputs);
    };

    const onSubmit = async (data: OktaIntegrationSchemaType) => {
        try {
            await createOktaIntegrationAccess({
                clientId: data.clientId,
                clientSecret: data.clientSecret,
                oktaDomain: data.oktaDomain,
            });
            await queryClient.invalidateQueries({ queryKey: ['integrationAccesses'] });
            setShowInputs(false);
        } catch (error) {
            console.error('Error sending integration data:', error);
        }
    };

    if (!showInputs) {
        return <Button onClick={toggleInputs}>{label}</Button>;
    } else {
        return (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Okta Domain</Label>
                    <Input
                        {...register('oktaDomain', { required: 'Okta Domain is required' })}
                        type="text"
                        placeholder="https://your-domain.okta.com"
                    />
                    {errors.oktaDomain && <span className="text-xs text-destructive">{errors.oktaDomain.message}</span>}
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Client ID</Label>
                    <Input {...register('clientId', { required: 'Client ID is required' })} type="text" placeholder="Client ID" />
                    {errors.clientId && <span className="text-xs text-destructive">{errors.clientId.message}</span>}
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">Client Secret</Label>
                    <Input
                        {...register('clientSecret', { required: 'Client Secret is required' })}
                        type="password"
                        placeholder="Client Secret"
                    />
                    {errors.clientSecret && <span className="text-xs text-destructive">{errors.clientSecret.message}</span>}
                </div>

                <Link
                    to="/tutorial/okta"
                    target="_blank"
                    className="flex items-center justify-between p-2 text-sm text-foreground border border-border rounded-md hover:bg-muted transition-colors"
                >
                    <span>Okta Tutorial</span>
                    <ExternalLink className="h-4 w-4" />
                </Link>

                <Button type="submit">Submit</Button>
            </form>
        );
    }
};
